/**
 * Calculates "smooth" percentile (using fractional ranks) - i.e. interpolate between elements - and includes endpoints.
 *
 * @param p is percentile expressed as ratio in interval [0..1] inclusive
 *
 *  @returns
 *  * undefined if the set is empty
 *  * smallest value in the set if p===0
 *  * largest value in the set if p===1
 *
 * References:
 * * This is equivalent of Excel's PERCENTILE.INC() function (https://docs.microsoft.com/en-us/dax/percentile-inc-function-dax) .
 * * According to Wikipedia, this method is recommended by NIST ( https://en.wikipedia.org/wiki/Percentile, section "Third_variant").
 *  */
export function smoothPercentileInclusive(values: number[], p: number): number | undefined {
  if (p < 0 || p > 1) {
    // the reason for this check is so that actual percents [0..100] are not passed in accidentally
    throw Error(`smoothPercentileInclusive(): bad parameter p=${p}, expected p∈[0..1]`);
  }
  if (values.length === 0) {
    return undefined;
  }

  let v = values.slice().sort((a, b) => a - b);
  let i = Math.floor((v.length - 1) * p);
  let r = (v.length - 1) * p - i;

  // this shouldn't be necessary if the inputs are correct, but never trust float calculations to get clean integers
  if (i < 0) {
    return v[0];
  } else if (i >= v.length - 1) {
    return v[v.length - 1];
  }

  return v[i] * (1 - r) + v[i + 1] * r;
}

/**
 * Calculate Teil-Sen estimate of a slope for a linear regression of a set of points.
 *
 * See https://en.wikipedia.org/wiki/Theil%E2%80%93Sen_estimator
 *
 * This variant allows the caller to specify a percentile instead of median, to get an estimated confidence interval for the slope.
 * E.g. if called twice with 0.05 and 0.95, it will provide upper and lower bounds of the 90% confidence interval. (90% of chance that
 * the slope is inside this interval)
 * Put in other words, if called with 0.95, it provides the 95th percentile, i.e. upper bound for the slope; such slope that  95% of possible slopes
 * are lower than this.
 * */
export function slopeEstimationPercentile(points: { x: number; y: number }[], percentile: number): number {
  let slopes = [];
  for (let i1 = 0; i1 < points.length; i1++) {
    let p1 = points[i1];
    for (let i2 = i1 + 1; i2 < points.length; i2++) {
      let p2 = points[i2];
      if (Math.abs(p2.x - p1.x) < 0.0001) {
        continue; // avoid division by zero
      }
      let slope = (p2.y - p1.y) / (p2.x - p1.x);
      slopes.push(slope);
    }
  }

  return smoothPercentileInclusive(slopes, percentile);
}

export function interceptEstimationPercentile(points: { x: number; y: number }[], slope: number, percentile: number): number {
  let intercepts: number[] = [];
  for (let i = 0; i < points.length; i++) {
    let p = points[i];
    let intercept = p.y - p.x * slope;
    intercepts.push(intercept);
  }
  return smoothPercentileInclusive(intercepts, percentile);
}
